import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useProviderSchema } from "../../../hooks/useAiAgents";
import { useTranslation } from "react-i18next";
import { Icon, InfoButton } from "../../ui";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { useIntercomContext } from "@finitless/shared/contexts";
import {
  resolveAgentSettingLabel,
  resolveSchemaDescription,
  resolveSchemaTitle,
} from "../../../lib/aiAgentI18n";
import {
  LANGUAGE_OPTIONS,
  normalizeLanguageCode,
  normalizeSettingsLanguage,
  supportsManagedLanguage,
} from "../../../lib/aiAgentLanguage";
import WhatsAppAuthSection from "./WhatsAppAuthSection";

// Fields that are locked when using OAuth (business_login)
const OAUTH_LOCKED_FIELDS = [
  "country_code",
  "phone_number",
  "access_token",
  "provider_external_id",
  "waba_id",
  "business_id",
  "meta_user_id",
  "connected_at",
];

const AUTH_TYPE_BUSINESS_LOGIN = "business_login";

const ProviderSettingsForm = ({
  provider,
  type,
  initialValues = {},
  onChange,
  readOnly = false,
  agent = null,
  onCreateAgent = null,
  autoConnectOAuth = false,
  onSaveBeforeConnect = null,
}) => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  const { data: schema, isLoading } = useProviderSchema(provider, type);
  const { showMessage, isEnabled } = useIntercomContext();
  const [errors, setErrors] = useState({});
  const isInternalUpdate = useRef(false);

  // Determine initial auth_type (use schema default if available)
  const schemaDefaultAuthType =
    schema?.properties?.auth_type?.default || "manual";
  const initialAuthType =
    initialValues.auth_type ||
    agent?.provider_settings?.auth_type ||
    schemaDefaultAuthType;

  // Store the original auth type from the server (doesn't change during editing)
  const originalAuthTypeRef = useRef(
    agent?.provider_settings?.auth_type || schemaDefaultAuthType,
  );

  // Fields that should be shared between auth types
  const SHARED_FIELDS = ["language_code"];

  // Maintain separate form state per auth type
  // Only pre-fill the state for the current auth type from endpoint
  // Shared fields (like language_code) are included in both states
  const [formDataByAuthType, setFormDataByAuthType] = useState(() => {
    const sharedValues = {};
    SHARED_FIELDS.forEach((field) => {
      if (initialValues[field] !== undefined) {
        sharedValues[field] = initialValues[field];
      }
    });

    return {
      manual:
        initialAuthType === "manual"
          ? { ...initialValues, auth_type: "manual" }
          : { auth_type: "manual", ...sharedValues },
      business_login:
        initialAuthType === AUTH_TYPE_BUSINESS_LOGIN
          ? { ...initialValues, auth_type: AUTH_TYPE_BUSINESS_LOGIN }
          : { auth_type: AUTH_TYPE_BUSINESS_LOGIN, ...sharedValues },
    };
  });
  const [currentAuthType, setCurrentAuthType] = useState(initialAuthType);

  // Derive current formData based on selected auth type
  const formData =
    formDataByAuthType[currentAuthType] || formDataByAuthType.manual;
  const authType = currentAuthType;
  const isWhatsApp = provider === "whatsapp";
  const isOAuthMode = authType === AUTH_TYPE_BUSINESS_LOGIN;

  // Handle auth type change from WhatsAppAuthSection
  const handleAuthTypeChange = useCallback(
    (newAuthType) => {
      setCurrentAuthType(newAuthType);
      // Notify parent with the state for the new auth type (includes shared fields)
      if (onChange) {
        isInternalUpdate.current = true;
        onChange(formDataByAuthType[newAuthType] || { auth_type: newAuthType });
      }
    },
    [formDataByAuthType, onChange],
  );

  /**
   * Helper to evaluate conditional schema attributes.
   * Used for x-visible-when, x-hidden-when, x-read-only-when.
   *
   * @param {Object} condition - { field: [values] } object
   * @returns {boolean} - true if condition is met
   */
  const evaluateCondition = useCallback(
    (condition) => {
      if (!condition || typeof condition !== "object") return false;

      for (const [conditionField, allowedValues] of Object.entries(condition)) {
        const currentValue =
          formData[conditionField] ||
          agent?.provider_settings?.[conditionField];
        if (
          !Array.isArray(allowedValues) ||
          !allowedValues.includes(currentValue)
        ) {
          return false;
        }
      }
      return true;
    },
    [formData, agent?.provider_settings],
  );

  // Determine if WhatsApp OAuth is connected
  const isOAuthConnected = agent?.configured === true;

  // Fields that should be hidden until OAuth connection is complete
  const OAUTH_HIDDEN_UNTIL_CONNECTED = [
    "provider_external_id",
    "country_code",
    "phone_number",
  ];

  // Language code is shown in the parent modal (CreateAgentModal/AgentDetailModal) for WhatsApp business_login
  // So always hide it here for WhatsApp business_login to avoid duplication
  const hideLanguageInProviderForm = isWhatsApp && isOAuthMode;

  /**
   * Determines if a field should be rendered at all.
   * Consolidates all "should render" logic in one place.
   *
   * A field is NOT rendered when:
   * - x-hidden: true (always hidden)
   * - x-hidden-when condition is met
   * - x-visible-when condition is NOT met
   * - It's auth_type for WhatsApp (handled by WhatsAppAuthSection)
   * - It's an OAuth-managed field for WhatsApp business_login and not yet connected
   */
  const shouldRenderField = useCallback(
    (fieldName, fieldSchema) => {
      // x-hidden: true → always hidden
      if (fieldSchema?.["x-hidden"] === true) {
        return false;
      }

      // x-hidden-when: { field: [values] } → hide when condition is met
      const hiddenWhen = fieldSchema?.["x-hidden-when"];
      if (hiddenWhen && evaluateCondition(hiddenWhen)) {
        return false;
      }

      // auth_type for WhatsApp is handled by WhatsAppAuthSection
      if (isWhatsApp && fieldName === "auth_type") {
        return false;
      }

      // For WhatsApp business_login, hide OAuth-managed fields until connected
      if (
        isWhatsApp &&
        isOAuthMode &&
        OAUTH_HIDDEN_UNTIL_CONNECTED.includes(fieldName) &&
        !isOAuthConnected
      ) {
        return false;
      }

      // Hide language_code here when it's shown in CreateAgentModal
      if (fieldName === "language_code" && hideLanguageInProviderForm) {
        return false;
      }

      // x-visible-when: { field: [values] } → show only when condition is met
      const visibleWhen = fieldSchema?.["x-visible-when"];
      if (visibleWhen && !evaluateCondition(visibleWhen)) {
        return false;
      }

      return true;
    },
    [
      evaluateCondition,
      isWhatsApp,
      isOAuthMode,
      isOAuthConnected,
      hideLanguageInProviderForm,
    ],
  );

  /**
   * Determines if a field should be disabled (rendered but not editable).
   *
   * A field is disabled when:
   * - Form is in readOnly mode
   * - readOnly: true (JSON Schema standard)
   * - x-read-only-when condition is met
   * - Field is locked by OAuth (WhatsApp business_login mode)
   */
  const isFieldDisabled = useCallback(
    (fieldName, fieldSchema) => {
      // Form-level readOnly
      if (readOnly) return true;

      // Schema-level readOnly (JSON Schema standard)
      if (fieldSchema?.readOnly === true) return true;

      // x-read-only-when: { field: [values] } → read-only when condition is met
      const readOnlyWhen = fieldSchema?.["x-read-only-when"];
      if (readOnlyWhen && evaluateCondition(readOnlyWhen)) {
        return true;
      }

      // OAuth locked fields (WhatsApp business_login)
      if (
        isWhatsApp &&
        isOAuthMode &&
        OAUTH_LOCKED_FIELDS.includes(fieldName)
      ) {
        return true;
      }

      return false;
    },
    [readOnly, evaluateCondition, isWhatsApp, isOAuthMode],
  );

  const effectiveSchema = useMemo(() => {
    const base = schema || { properties: {}, required: [], hasSettings: false };
    const properties = { ...(base.properties || {}) };
    const isWhatsApp = provider === "whatsapp";
    const isManagedLanguageProvider = supportsManagedLanguage(provider);

    const hasExternalId = Object.prototype.hasOwnProperty.call(
      properties,
      "provider_external_id",
    );
    let addedExternal = false;
    if (isWhatsApp && !hasExternalId) {
      properties.provider_external_id = {
        title: t("settingsForm.whatsappExternalIdTitle"),
        type: "string",
        pattern: "^[0-9]+$",
        minLength: 1,
        description: t("settingsForm.whatsappExternalIdDescription"),
        examples: ["123456"],
      };
      addedExternal = true;
    }

    const hasLanguageCode = Object.prototype.hasOwnProperty.call(
      properties,
      "language_code",
    );
    let addedLanguage = false;
    if (isManagedLanguageProvider && !hasLanguageCode) {
      properties.language_code = {
        title: t("settingsForm.languageTitle"),
        type: "string",
        enum: LANGUAGE_OPTIONS.map((option) => option.value),
        description: t("settingsForm.languageDescription"),
        examples: ["en"],
      };
      addedLanguage = true;
    }

    const required = Array.isArray(base.required) ? [...base.required] : [];
    if (isWhatsApp && !required.includes("provider_external_id")) {
      required.push("provider_external_id");
    }
    if (isManagedLanguageProvider && !required.includes("language_code")) {
      required.push("language_code");
    }

    return {
      ...base,
      properties,
      required,
      hasSettings: base.hasSettings || addedExternal || addedLanguage,
    };
  }, [schema, provider, t]);

  // Calculate required fields based on conditional schema (if/then/else)
  const requiredFields = useMemo(() => {
    if (!schema) return effectiveSchema.required || [];

    // Check for conditional required using if/then/else
    const ifCondition = schema.if;
    const thenRequired = schema.then?.required || [];
    const elseRequired = schema.else?.required || [];

    if (ifCondition && ifCondition.properties) {
      // Evaluate the if condition based on current form data
      const conditionField = Object.keys(ifCondition.properties)[0];
      const conditionValue = ifCondition.properties[conditionField]?.const;

      if (conditionField && conditionValue !== undefined) {
        const currentValue = formData[conditionField];
        const conditionalRequired =
          currentValue === conditionValue ? thenRequired : elseRequired;

        // Merge with effectiveSchema.required (which includes added fields like provider_external_id)
        const baseRequired = effectiveSchema.required || [];
        const merged = [...new Set([...conditionalRequired, ...baseRequired])];

        // For business_login, remove fields that are OAuth-managed
        if (currentValue === AUTH_TYPE_BUSINESS_LOGIN) {
          return merged.filter(
            (field) =>
              !OAUTH_LOCKED_FIELDS.includes(field) ||
              field === "auth_type" ||
              field === "language_code",
          );
        }

        return merged;
      }
    }

    // Fallback to effectiveSchema.required
    return effectiveSchema.required || [];
  }, [schema, effectiveSchema.required, formData]);

  useEffect(() => {
    // Skip re-initialization when the change originated from this component
    // (auth type switch or field input), to avoid a feedback loop that wipes data
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const normalized = normalizeSettingsLanguage(provider, initialValues);
    const initAuthType =
      normalized.auth_type ||
      agent?.provider_settings?.auth_type ||
      schemaDefaultAuthType;

    // Only pre-fill the state for the current auth type from endpoint
    // Extract shared field values
    const sharedValues = {};
    SHARED_FIELDS.forEach((field) => {
      if (normalized[field] !== undefined) {
        sharedValues[field] = normalized[field];
      }
    });

    setFormDataByAuthType({
      manual:
        initAuthType === "manual"
          ? { ...normalized, auth_type: "manual" }
          : { auth_type: "manual", ...sharedValues },
      business_login:
        initAuthType === AUTH_TYPE_BUSINESS_LOGIN
          ? { ...normalized, auth_type: AUTH_TYPE_BUSINESS_LOGIN }
          : { auth_type: AUTH_TYPE_BUSINESS_LOGIN, ...sharedValues },
    });
    setCurrentAuthType(initAuthType);
  }, [
    initialValues,
    provider,
    agent?.provider_settings?.auth_type,
    schemaDefaultAuthType,
  ]);

  const handleInputChange = (fieldName, value) => {
    if (fieldName === "language_code" && supportsManagedLanguage(provider)) {
      value = normalizeLanguageCode(value);
    }

    // Check if this field has a numeric pattern and filter non-numeric characters
    if (
      effectiveSchema &&
      effectiveSchema.properties &&
      effectiveSchema.properties[fieldName]
    ) {
      const fieldSchema = effectiveSchema.properties[fieldName];
      if (fieldSchema.pattern === "^[0-9]+$") {
        // Only allow numeric characters
        value = value.replace(/[^0-9]/g, "");
      }
    }

    const newFormData = { ...formData, [fieldName]: value };

    // Update state - sync shared fields across both auth types
    setFormDataByAuthType((prev) => {
      const updated = {
        ...prev,
        [currentAuthType]: newFormData,
      };

      // If this is a shared field, sync it to the other auth type
      if (SHARED_FIELDS.includes(fieldName)) {
        const otherAuthType =
          currentAuthType === "manual" ? AUTH_TYPE_BUSINESS_LOGIN : "manual";
        updated[otherAuthType] = {
          ...prev[otherAuthType],
          [fieldName]: value,
        };
      }

      return updated;
    });

    // Validate field
    validateField(fieldName, value);

    // Notify parent component
    if (onChange) {
      isInternalUpdate.current = true;
      onChange(newFormData);
    }
  };

  const validateField = (fieldName, value) => {
    if (
      !effectiveSchema ||
      !effectiveSchema.properties ||
      !effectiveSchema.properties[fieldName]
    )
      return;

    const fieldSchema = effectiveSchema.properties[fieldName];
    const newErrors = { ...errors };
    const normalizedValue =
      typeof value === "string" ? value : String(value ?? "");

    // Required validation
    if (
      requiredFields.includes(fieldName) &&
      (!normalizedValue || normalizedValue.trim() === "")
    ) {
      newErrors[fieldName] = t("settingsForm.validation.required", {
        field: fieldSchema.title || fieldName,
      });
    } else {
      delete newErrors[fieldName];
    }

    // Skip validation if value is empty and not required
    if (!normalizedValue || normalizedValue.trim() === "") {
      setErrors(newErrors);
      return;
    }

    // Length validation using schema constraints
    if (
      fieldSchema.minLength &&
      normalizedValue.length < fieldSchema.minLength
    ) {
      newErrors[fieldName] = t("settingsForm.validation.minLength", {
        field: fieldSchema.title || fieldName,
        min: fieldSchema.minLength,
      });
    }

    if (
      fieldSchema.maxLength &&
      normalizedValue.length > fieldSchema.maxLength
    ) {
      newErrors[fieldName] = t("settingsForm.validation.maxLength", {
        field: fieldSchema.title || fieldName,
        max: fieldSchema.maxLength,
      });
    }

    // Allowed values validation (e.g. language_code enum)
    if (
      Array.isArray(fieldSchema.enum) &&
      !fieldSchema.enum.includes(normalizedValue)
    ) {
      newErrors[fieldName] = t("settingsForm.validation.invalidFormat", {
        field: fieldSchema.title || fieldName,
      });
    }

    // Pattern validation using schema pattern
    if (fieldSchema.pattern) {
      const regex = new RegExp(fieldSchema.pattern);
      if (!regex.test(normalizedValue)) {
        newErrors[fieldName] = t("settingsForm.validation.invalidFormat", {
          field: fieldSchema.title || fieldName,
        });
      }
    }

    setErrors(newErrors);
  };

  const renderField = (fieldName, fieldSchema) => {
    // Check if field should be rendered at all
    if (!shouldRenderField(fieldName, fieldSchema)) {
      return null;
    }

    const isManagedLanguageField =
      fieldName === "language_code" && supportsManagedLanguage(provider);
    const rawValue = formData[fieldName] || "";
    const value = isManagedLanguageField
      ? normalizeLanguageCode(rawValue)
      : rawValue;
    const error = errors[fieldName];
    const isRequired = requiredFields.includes(fieldName);
    const isNumericField = fieldSchema.pattern === "^[0-9]+$";
    const localizedFieldLabel = resolveAgentSettingLabel({
      key: fieldName,
      title: fieldSchema.title,
      t,
      locale,
    });
    const localizedDescription = resolveSchemaDescription({
      description: fieldSchema.description,
      provider,
      type,
      t,
      locale,
    });

    // Check if field should be disabled
    const fieldDisabled = isFieldDisabled(fieldName, fieldSchema);

    return (
      <div key={fieldName} className="space-y-2">
        <label className="flex items-center text-sm font-medium text-gray-300">
          {localizedFieldLabel}
          {isRequired && !fieldDisabled && (
            <span className="ml-1 text-red-400">*</span>
          )}
          <InfoButton
            examples={fieldSchema.examples}
            description={localizedDescription}
          />
        </label>

        {isManagedLanguageField ? (
          <select
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            disabled={fieldDisabled}
            className={`custom-select w-full rounded-md border bg-gray-800 px-3 py-2 text-white ${
              error
                ? "border-red-500 focus:border-red-400"
                : "border-gray-600 focus:border-blue-500"
            } ${fieldDisabled ? "cursor-not-allowed opacity-50" : "focus:ring-1 focus:ring-blue-500 focus:outline-none"} `}
          >
            {LANGUAGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {t(`settingsForm.languageOptions.${option.value}`)}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={fieldSchema.type === "string" ? "text" : fieldSchema.type}
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            disabled={fieldDisabled}
            placeholder={
              fieldSchema.examples && fieldSchema.examples[0]
                ? fieldSchema.examples[0]
                : ""
            }
            minLength={fieldSchema.minLength}
            maxLength={fieldSchema.maxLength}
            pattern={fieldSchema.pattern}
            inputMode={isNumericField ? "numeric" : "text"}
            className={`w-full rounded-md border bg-gray-800 px-3 py-2 text-white placeholder-gray-500 ${
              error
                ? "border-red-500 focus:border-red-400"
                : "border-gray-600 focus:border-blue-500"
            } ${fieldDisabled ? "cursor-not-allowed opacity-50" : "focus:ring-1 focus:ring-blue-500 focus:outline-none"} `}
          />
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        {isManagedLanguageField && !fieldDisabled && isEnabled && (
          <button
            type="button"
            onClick={() =>
              showMessage(t("settingsForm.newLanguageIntercomPrefill"))
            }
            className="text-sm text-blue-400 transition-colors hover:text-blue-300 hover:underline"
          >
            {t("settingsForm.newLanguageCta")}
          </button>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/4 rounded bg-gray-700"></div>
          <div className="h-10 rounded bg-gray-700"></div>
        </div>
        <div className="animate-pulse">
          <div className="mb-2 h-4 w-1/4 rounded bg-gray-700"></div>
          <div className="h-10 rounded bg-gray-700"></div>
        </div>
      </div>
    );
  }

  if (!effectiveSchema || !effectiveSchema.hasSettings) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-2 text-lg font-semibold text-white">
          {resolveSchemaTitle({
            title: effectiveSchema.title,
            provider,
            type,
            t,
            locale,
          })}
        </h3>
        {effectiveSchema.description && (
          <p className="text-sm text-gray-400">
            {resolveSchemaDescription({
              description: effectiveSchema.description,
              provider,
              type,
              t,
              locale,
            })}
          </p>
        )}
      </div>

      {/* WhatsApp Auth Section - shown before other fields */}
      {isWhatsApp && (
        <>
          <WhatsAppAuthSection
            agent={agent}
            authType={authType}
            onAuthTypeChange={handleAuthTypeChange}
            onCreateAgent={onCreateAgent}
            onSaveBeforeConnect={onSaveBeforeConnect}
            autoConnect={autoConnectOAuth}
            readOnly={readOnly}
            editedPin={initialValues?.pin}
          />

          {/* Warning when auth type changed from original */}
          {currentAuthType !== originalAuthTypeRef.current && (
            <div className="rounded-lg border border-yellow-800 bg-yellow-900/20 p-3">
              <div className="flex items-start space-x-3">
                <Icon
                  name="AlertTriangle"
                  className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500"
                />
                <p className="text-sm text-yellow-300">
                  {t("whatsappOAuth.authTypeChangeWarning")}
                </p>
              </div>
            </div>
          )}
        </>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Object.entries(effectiveSchema.properties || {})
          .sort(([a], [b]) => {
            // provider_external_id should come first for WhatsApp
            if (a === "provider_external_id") return -1;
            if (b === "provider_external_id") return 1;
            return 0;
          })
          .map(([fieldName, fieldSchema]) =>
            renderField(fieldName, fieldSchema),
          )}
      </div>
    </div>
  );
};

export default ProviderSettingsForm;
